import React, { useState } from 'react';
import {
    Video, Package, ExternalLink, X, Clock, User, Heart, Eye,
    DollarSign, TrendingUp, RefreshCw, AlertCircle, ChevronDown, ChevronUp, Pencil
} from 'lucide-react';
import AIPCcModal from './AIPCcModal';

const tryParse = (val) => {
    if (!val) return null;
    if (typeof val === 'object') return val;
    try { return JSON.parse(val); } catch { return null; }
};

const statusConfig = {
    completed: { dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', label: 'Completed' },
    processing: { dot: 'bg-blue-500 animate-pulse', text: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200', label: 'Processing' },
    pending: { dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', label: 'Pending' },
    failed: { dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', label: 'Failed' },
};

/**
 * Full AIP Video Card - detailed view for an ASIN group.
 * Shows all data sections, all videos for this ASIN, links, etc.
 */
const ccLabel = (cc) =>
    cc === undefined || cc === 'loading' ? '…'
        : cc === 'error' ? '?'
            : cc.count > 0 ? `✓${cc.count}${cc.highestCommission != null ? ` (${cc.highestCommission}%)` : ''}`
                : '0';

const AIPVideoCard = ({ videos, primary, cc, onClose, mode = 'storefront', storefrontName, rawStorefrontName, onPlacementNameChange }) => {
    const [selectedVideo, setSelectedVideo] = useState(primary?.id || videos[0]?.id);
    const [editingPlacement, setEditingPlacement] = useState(false);
    const [tempPlacementName, setTempPlacementName] = useState(storefrontName || '');

    const video = videos.find(v => v.id === selectedVideo) || primary || videos[0];
    const videoMeta = tryParse(video.video_meta);
    const commission = tryParse(video.commission_data);
    const product = tryParse(video.product_data);
    const sales = tryParse(video.sales_data);
    const parts = tryParse(video.parts_data);
    const videosData = tryParse(video.videos_data);
    const [enlargedImage, setEnlargedImage] = useState(false);
    const [showCc, setShowCc] = useState(false);
    const sc = statusConfig[video.status] || statusConfig.pending;

    const isStorefront = mode === 'storefront';

    // Carousel stats
    const upperAvg = videosData?.upper_carousel?.avg_videos_per_fetch ?? null;
    const upperAvgInf = videosData?.upper_carousel?.avg_influencer_per_fetch ?? null;
    const upperAvgNonInf = upperAvg != null && upperAvgInf != null ? parseFloat((upperAvg - upperAvgInf).toFixed(1)) : null;
    const bottomAvg = videosData?.bottom_carousel?.avg_videos_per_fetch ?? null;
    const bottomAvgInf = videosData?.bottom_carousel?.avg_influencer_per_fetch ?? null;
    const bottomAvgNonInf = bottomAvg != null && bottomAvgInf != null ? parseFloat((bottomAvg - bottomAvgInf).toFixed(1)) : null;

    // Storefront placement - box uses user's editable name, badge uses raw storefront name
    const getPlacementFor = (name) => {
        if (!name || !videosData) return null;
        const n = name.toLowerCase().trim();
        const normalize = s => s.replace(/[^a-z]/gi, '').toLowerCase();
        const upperCount = (videosData.upper_carousel?.influencer_names || [])
            .filter(x => typeof x === 'string' && normalize(x).includes(normalize(n))).length;
        const bottomCount = (videosData.bottom_carousel?.influencer_names || [])
            .filter(x => typeof x === 'string' && normalize(x).includes(normalize(n))).length;
        const parts = [];
        if (upperCount > 0) parts.push(`TOP(${upperCount})`);
        if (bottomCount > 0) parts.push(`BOTTOM(${bottomCount})`);
        if (parts.length === 0) return { label: 'NONE', color: '#991b1b' };
        if (parts.length === 2) return { label: `BOTH(${upperCount + bottomCount})`, color: '#166534' };
        return { label: parts.join(' '), color: '#92400e' };
    };
    const getStorefrontPlacement = () => {
        if (!storefrontName || !videosData) return null;
        const name = storefrontName.toLowerCase().trim();
        const normalize = s => s.replace(/[^a-z]/gi, '').toLowerCase();
        const upperCount = (videosData.upper_carousel?.influencer_names || [])
            .filter(n => typeof n === 'string' && normalize(n).includes(normalize(name))).length;
        const bottomCount = (videosData.bottom_carousel?.influencer_names || [])
            .filter(n => typeof n === 'string' && normalize(n).includes(normalize(name))).length;
        const parts = [];
        if (upperCount > 0) parts.push(`TOP(${upperCount})`);
        if (bottomCount > 0) parts.push(`BOTTOM(${bottomCount})`);
        if (parts.length === 0) return { label: 'NONE', color: '#991b1b' };
        if (parts.length === 2) return { label: `BOTH(${upperCount + bottomCount})`, color: '#166534' };
        return { label: parts.join(' '), color: '#92400e' };
    };
    const placement = video.status === 'completed' ? getStorefrontPlacement() : null;
    const badgePlacement = video.status === 'completed' ? getPlacementFor(rawStorefrontName) : null;
    const thumbnailUrl = video.image || videoMeta?.image_url;
    const displayTitle = isStorefront
        ? (videoMeta?.video_title || video.title || 'Untitled')
        : (video.title || videoMeta?.video_title || 'Untitled');
    const siblingCount = isStorefront ? videos.length - 1 : 0;

    const price = commission?.price != null && !isNaN(Number(commission.price)) ? Number(commission.price) : null;
    const commRate = commission?.commission_rate != null && !isNaN(Number(commission.commission_rate)) ? Number(commission.commission_rate) : null;
    const commPerSale = commission?.commission_per_sale != null && !isNaN(Number(commission.commission_per_sale)) ? Number(commission.commission_per_sale) : null;
    const estSales = sales?.estimated_monthly_sales != null && Number(sales.estimated_monthly_sales) > 0 ? Number(sales.estimated_monthly_sales) : null;
    const estRev = sales?.estimated_monthly_revenue != null && Number(sales.estimated_monthly_revenue) > 0 ? Number(sales.estimated_monthly_revenue) : null;
    const bsr = sales?.sales_rank != null && Number(sales.sales_rank) > 0 ? Number(sales.sales_rank) : null;

    return (
        <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-gray-200">
            {/* Header */}
            <div className={`px-4 py-3 ${sc.bg} border-b ${sc.border} flex items-center justify-between`}>
                <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="relative flex-shrink-0">
                        {thumbnailUrl ? (
                            <img src={thumbnailUrl} alt="" onClick={() => setEnlargedImage(true)} className="w-14 h-14 object-contain rounded-lg border border-gray-200 bg-white p-0.5 cursor-pointer" />
                        ) : (
                            <div className="w-14 h-14 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg border border-gray-200 flex items-center justify-center">
                                <Video size={20} className="text-gray-300" />
                            </div>
                        )}
                        {isStorefront && siblingCount > 0 && (
                            <div className="absolute -bottom-1 -right-1 bg-purple-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[9px] font-bold border-2 border-white shadow">
                                +{siblingCount}
                            </div>
                        )}
                    </div>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                            {video.asin && (
                                <span className="font-mono text-[11px] font-bold bg-white/80 text-gray-700 px-1.5 py-0.5 rounded border border-gray-200">
                                    {video.asin}
                                </span>
                            )}
                            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase ${sc.text} bg-white/60`}>
                                {sc.label}
                            </span>
                            {isStorefront && badgePlacement && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border"
                                    style={{ color: badgePlacement.color, borderColor: badgePlacement.color, background: badgePlacement.color + '15' }}>
                                    {badgePlacement.label}
                                </span>
                            )}
                        </div>
                        <p className="text-sm font-bold text-gray-800 leading-tight line-clamp-2">{displayTitle}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {isStorefront ? (
                                <>
                                    {videoMeta?.vendor_name && (
                                        <span className="text-[10px] text-gray-500 font-semibold flex items-center gap-0.5"><User size={9} /> {videoMeta.vendor_name}</span>
                                    )}
                                    {videoMeta?.duration && (
                                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5"><Clock size={9} /> {videoMeta.duration}</span>
                                    )}
                                    {videoMeta?.heart_count > 0 && (
                                        <span className="text-[10px] text-pink-500 flex items-center gap-0.5"><Heart size={9} /> {videoMeta.heart_count}</span>
                                    )}
                                    {product?.brand && (
                                        <span className="text-[10px] text-gray-400">{product.brand}</span>
                                    )}
                                    {video.created_at && (
                                        <span className="hidden lg:block text-[9px] text-black text-center leading-tight">
                                            Found at {new Date(video.created_at).toLocaleDateString()}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    {product?.brand && (
                                        <span className="text-[10px] text-gray-500 font-semibold">{product.brand}</span>
                                    )}
                                    {product?.category && (
                                        <span className="text-[10px] text-gray-400 bg-white/60 px-1.5 py-0.5 rounded-full">{product.category}</span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {onClose && (
                    <button onClick={onClose} className="p-1.5 hover:bg-white/50 rounded-lg transition flex-shrink-0">
                        <X size={16} className="text-gray-500" />
                    </button>
                )}
            </div>

            {/* Key Metrics Row */}
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <div className="grid grid-cols-3 md:grid-cols-7 gap-1.5">
                    <MetricBox label="TOP" value={upperAvg != null ? `${upperAvg}=${upperAvgNonInf}+${upperAvgInf}` : '-'} icon={<Eye size={12} />} color="purple" />
                    <MetricBox label="BOTTOM" value={bottomAvg != null ? `${bottomAvg}=${bottomAvgNonInf}+${bottomAvgInf}` : '-'} icon={<Eye size={12} />} color="purple" />
                    <MetricBox label="Total Inf." value={videosData?.total_inf_videos != null ? videosData.total_inf_videos : '-'} icon={<Video size={12} />} color="sky" />
                    <div className="bg-white rounded-lg border border-purple-200 px-2 py-1.5 text-center">
                        <div className="text-[8px] uppercase font-semibold text-gray-400 flex items-center justify-center gap-0.5">
                            <span className="opacity-60"><TrendingUp size={12} /></span> My Placement
                        </div>
                        <div className="text-sm font-bold" style={{ color: placement && placement.color }}>{placement && placement.label}</div>
                        <div className="flex items-center justify-center gap-0.5">
                            {editingPlacement ? (
                                <input
                                    type="text"
                                    value={tempPlacementName}
                                    onChange={e => setTempPlacementName(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') { onPlacementNameChange(tempPlacementName); setEditingPlacement(false); }
                                        if (e.key === 'Escape') setEditingPlacement(false);
                                    }}
                                    onBlur={() => { onPlacementNameChange(tempPlacementName); setEditingPlacement(false); }}
                                    autoFocus
                                    className="w-full text-[9px] text-center border border-teal-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                                    onClick={e => e.stopPropagation()}
                                />
                            ) : (
                                <>
                                    <span className="text-[8px] text-gray-400 truncate max-w-[70px]">{storefrontName || '-'}</span>
                                    {onPlacementNameChange && (
                                        <button onClick={(e) => { e.stopPropagation(); setTempPlacementName(storefrontName || ''); setEditingPlacement(true); }}
                                            className="p-0.5 flex-shrink-0">
                                            <Pencil size={11} className="text-gray-400" />
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <MetricBox label="Price" value={price != null ? `$${price.toFixed(2)}` : '-'} icon={<DollarSign size={12} />} color="emerald" />
                    <MetricBox label="Sales/mo" value={estSales != null ? estSales : '-'} icon={<TrendingUp size={12} />} color="blue" />
                    <button type="button" disabled={!(cc?.count > 0)}
                        onClick={e => { e.stopPropagation(); setShowCc(true); }}
                        title={cc?.count > 0 ? 'View campaigns' : undefined}
                        className={`grid rounded-lg transition ${cc?.count > 0 ? 'cursor-pointer hover:ring-2 hover:ring-green-300' : 'cursor-default'}`}>
                        <MetricBox label="CC Camp." value={ccLabel(cc)} icon={<DollarSign size={12} />} customColor={cc?.count > 0 ? '#166534' : '#9ca3af'} />
                    </button>
                </div>
            </div>

            <div className="px-4 py-3 space-y-3">
                {/* Influencer Videos + Sales Performance - side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Influencer Videos on Product Page */}
                    {videosData && videosData.total_inf_videos > 0 && (
                        <Section title="Influencer Videos on Product Page" icon={<Eye size={12} />}>
                            <div className="grid grid-cols-2 gap-1.5 mb-2">
                                <InfoPill label="Total Inf." value={videosData.total_inf_videos} color="purple" />
                                {videosData.detected_parent_asin && (
                                    <InfoPill label="Parent ASIN" value={videosData.detected_parent_asin} />
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-1.5">
                                {videosData.upper_carousel && (
                                    <CarouselInfo label="Upper Carousel" data={videosData.upper_carousel} />
                                )}
                                {videosData.bottom_carousel && (
                                    <CarouselInfo label="Bottom Carousel" data={videosData.bottom_carousel} />
                                )}
                            </div>
                        </Section>
                    )}

                    {/* Sales Performance */}
                    {sales && Object.keys(sales).length > 0 && (
                        <Section title="Sales Performance" icon={<TrendingUp size={12} />}>
                            <div className="grid grid-cols-2 gap-1.5">
                                {Number(sales.sales_rank) > 0 && <InfoPill label="BSR" value={`#${Number(sales.sales_rank).toLocaleString()}`} color="blue" />}
                                {Number(sales.monthly_sold) > 0 && <InfoPill label="Monthly Sold" value={Number(sales.monthly_sold).toLocaleString()} color="blue" />}
                                {Number(sales.estimated_monthly_sales) > 0 && <InfoPill label="Est. Sales/mo" value={sales.estimated_monthly_sales} color="indigo" />}
                                {Number(sales.estimated_monthly_revenue) > 0 && <InfoPill label="Est. Rev/mo" value={`$${Number(sales.estimated_monthly_revenue).toLocaleString()}`} color="purple" />}
                                {Number(sales.sales_rank_drops_30) > 0 && <InfoPill label="Drops 30d" value={sales.sales_rank_drops_30} color="sky" />}
                                {Number(sales.sales_rank_drops_90) > 0 && <InfoPill label="Drops 90d" value={sales.sales_rank_drops_90} color="sky" />}
                            </div>
                        </Section>
                    )}
                </div>

                {/* Parts & Bundles */}
                {parts && Object.keys(parts).length > 0 && (Array.isArray(parts.included_components) && parts.included_components.length > 0 || parts.frequently_bought_together) && (
                    <Section title="Parts & Bundles" icon={<Package size={12} />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                            {Array.isArray(parts.included_components) && parts.included_components.length > 0 && (
                                <InfoPill label="Included" value={parts.included_components.join(', ')} />
                            )}
                            {parts.frequently_bought_together && (
                                <InfoPill label="Bought Together" value={String(parts.frequently_bought_together)} />
                            )}
                        </div>
                    </Section>
                )}

                {/* Videos for this ASIN (storefront: sibling videos; reverse: hidden since 1:1) */}
                {isStorefront && videos.length > 1 && (
                    <Section title={`All Videos on this ASIN (${videos.length})`} icon={<Video size={13} />}>
                        <div className="space-y-2">
                            {videos.map((v) => {
                                const vm = tryParse(v.video_meta);
                                const vsc = statusConfig[v.status] || statusConfig.pending;
                                const isSelected = v.id === selectedVideo;
                                return (
                                    <div
                                        key={v.id}
                                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${isSelected
                                            ? 'bg-teal-50 border-2 border-teal-300 shadow-sm'
                                            : 'bg-gray-50 border border-gray-100 hover:border-gray-300'
                                            }`}
                                        onClick={() => setSelectedVideo(v.id)}
                                    >
                                        {/* Video thumbnail */}
                                        {(v.image || vm?.image_url) ? (
                                            <img src={v.image || vm?.image_url} alt="" className="w-10 h-10 object-contain rounded border bg-white flex-shrink-0" />
                                        ) : (
                                            <div className="w-10 h-10 bg-gray-100 rounded border flex items-center justify-center flex-shrink-0">
                                                <Video size={14} className="text-gray-300" />
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-700 truncate">
                                                {vm?.video_title || v.title || 'Untitled'}
                                            </p>
                                            <div className="flex items-center gap-2 text-[10px] text-gray-400 mt-0.5">
                                                {vm?.vendor_name && (
                                                    <span className="flex items-center gap-0.5"><User size={8} /> {vm.vendor_name}</span>
                                                )}
                                                {vm?.duration && (
                                                    <span className="flex items-center gap-0.5"><Clock size={8} /> {vm.duration}</span>
                                                )}
                                                {vm?.heart_count > 0 && (
                                                    <span className="flex items-center gap-0.5"><Heart size={8} /> {vm.heart_count}</span>
                                                )}
                                                {vm?.creator_type && (
                                                    <span className="bg-gray-100 px-1.5 py-0.5 rounded text-[9px]">{vm.creator_type}</span>
                                                )}
                                            </div>
                                        </div>
                                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${vsc.text} ${vsc.bg}`}>
                                            {vsc.label}
                                        </span>
                                        {vm?.vdp_url && (
                                            <a
                                                href={vm.vdp_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-purple-500 hover:text-purple-700 flex-shrink-0 p-1"
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <ExternalLink size={14} />
                                            </a>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Section>
                )}

                {/* Pending / Processing empty state */}
                {!product && !commission && !sales && video.status !== 'failed' && (
                    <div className="bg-gray-50 rounded-xl p-8 text-center border border-gray-100">
                        <RefreshCw size={28} className={`mx-auto text-gray-300 mb-3 ${video.status === 'processing' ? 'animate-spin' : ''}`} />
                        <p className="text-sm text-gray-500 font-medium">
                            {video.status === 'processing' ? 'Analyzing this product...' : 'Waiting to be processed.'}
                        </p>
                    </div>
                )}

                {/* Error */}
                {video.status === 'failed' && video.error_message && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                        <AlertCircle size={18} className="text-red-400 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-red-700">{video.error_message}</p>
                    </div>
                )}

                {showCc && (
                    <AIPCcModal asin={video.asin} title={displayTitle} image={thumbnailUrl} cc={cc} onClose={() => setShowCc(false)} />
                )}

                {enlargedImage && (
                    <div onClick={() => setEnlargedImage(false)} className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center cursor-pointer">
                        <img src={thumbnailUrl} alt="" className="max-h-[80vh] max-w-[80vw] object-contain rounded-xl shadow-2xl" />
                    </div>
                )}

                {/* Links bar */}
                <div className="flex gap-2 flex-wrap pt-2 border-t border-gray-100">
                    {video.asin && (
                        <a
                            href={`https://www.amazon.com/dp/${video.asin}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition"
                        >
                            <Package size={13} /> View on Amazon <ExternalLink size={10} />
                        </a>
                    )}
                    {videoMeta?.vdp_url && (
                        <a
                            href={videoMeta.vdp_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-lg bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition"
                        >
                            <Video size={13} /> Watch Video <ExternalLink size={10} />
                        </a>
                    )}
                    {video.processed_at && (
                        <span className="ml-auto text-[10px] text-gray-400 self-center">
                            Processed {new Date(video.processed_at).toLocaleDateString()}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ── Shared UI ── */
const Section = ({ title, icon, children }) => (
    <div>
        <div className="flex items-center gap-1 mb-1.5">
            <span className="text-gray-400">{icon}</span>
            <span className="text-[9px] uppercase font-bold tracking-wider text-gray-400">{title}</span>
            <div className="flex-1 h-px bg-gray-100 ml-1" />
        </div>
        {children}
    </div>
);

const pillColors = {
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    sky: 'bg-sky-50 border-sky-200 text-sky-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    teal: 'bg-teal-50 border-teal-200 text-teal-700',
};

const InfoPill = ({ label, value, color = 'gray' }) => (
    <div className={`rounded border px-2 py-1 ${pillColors[color] || pillColors.gray}`}>
        <div className="text-[8px] uppercase font-semibold opacity-60 truncate">{label}</div>
        <div className="text-[11px] font-bold truncate">{value}</div>
    </div>
);

const metricColors = {
    gray: 'border-gray-200',
    emerald: 'border-emerald-200',
    blue: 'border-blue-200',
    purple: 'border-purple-200',
    sky: 'border-sky-200',
};

const metricTextColors = {
    gray: 'text-gray-800',
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
    sky: 'text-sky-700',
};

const MetricBox = ({ label, value, sub, icon, color = 'gray', customColor }) => (
    <div className={`bg-white rounded-lg border ${metricColors[color]} px-2 py-1.5 text-center`}>
        <div className="text-[8px] uppercase font-semibold text-gray-400 flex items-center justify-center gap-0.5">
            <span className="opacity-60">{icon}</span> {label}
        </div>
        <div className={`text-sm font-bold ${customColor ? '' : metricTextColors[color]}`} style={customColor ? { color: customColor } : undefined}>{value}</div>
        {sub && <div className="text-[8px] text-gray-400">{sub}</div>}
    </div>
);

const CarouselInfo = ({ label, data }) => (
    <div className="bg-gray-50 rounded border border-gray-100 p-2">
        <div className="text-[9px] uppercase font-bold text-gray-400 mb-1">{label}</div>
        <div className="grid grid-cols-2 gap-1 text-[11px]">
            <div><span className="text-gray-400">Total:</span> <span className="font-semibold">{data.total_unique_videos ?? '-'}</span></div>
            <div><span className="text-gray-400">Influencer:</span> <span className="font-semibold text-purple-700">{data.total_unique_influencer_videos ?? '-'}</span></div>
            {data.avg_videos_per_fetch > 0 && (
                <div><span className="text-gray-400">Avg/Fetch:</span> <span className="font-semibold">{Number(data.avg_videos_per_fetch).toFixed(1)}</span></div>
            )}
            {data.avg_influencer_per_fetch > 0 && (
                <div><span className="text-gray-400">Avg Inf:</span> <span className="font-semibold">{Number(data.avg_influencer_per_fetch).toFixed(1)}</span></div>
            )}
        </div>
        {Array.isArray(data.influencer_names) && data.influencer_names.length > 0 && (
            <div className="mt-1.5 pt-1.5 border-t border-gray-200">
                <div className="text-[8px] uppercase text-gray-400 font-semibold mb-0.5">Influencers</div>
                <div className="flex flex-wrap gap-0.5 max-h-16 overflow-y-auto">
                    {data.influencer_names.map((name, i) => (
                        <span key={i} className="text-[9px] bg-purple-50 text-purple-700 px-1 py-0.5 rounded-full border border-purple-200">{name}</span>
                    ))}
                </div>
            </div>
        )}
    </div>
);

export default AIPVideoCard;
